import type { Artifact, CustomDataArtifact } from '@dev-streetbeat/chat';
import { create } from "./store";
import useAppStatusStore from "./appStatusStore";
import { getPortfolioId, isFinecoPortfolioArtifact } from "utils/portfolioUtils";
import {
  ConstraintSettingGeneric,
  ConstraintType,
} from "types/ConstraintsTypes";
import { ConstraintsService } from "shared/services/ConstraintsService";
import {
  applyInitialConstraints,
  handleConstraintChange,
  mergeAssetData,
} from "utils/constraintsUtils";

interface ConstraintState {
  messageId: string;
  data: ConstraintType["data"];
}
type PortfolioCreationType = {
  messageId: string;
  hasCreated: boolean;
};
type ActivityPanelStoreState = {
  constraintsState: any;
  parsedConfigConstraintsData: ConstraintSettingGeneric[];
  savedConstraintsState: any;
  constraintChangesUpdate: Array<{
    key: string;
    values: Array<{
      key: string;
    }>;
    seen: boolean;
  }>;
  isPortfolioCreated: PortfolioCreationType;
  portfolioMsgIds: string[];
  persistentConstraintsState: any;
  savedSentConstraintsStore: ConstraintState | null;
  constraintsMainMenuRedDot: boolean;
  attachmentsRedDot: boolean;
  portfoliosRedDot: boolean;
  financialPlanStore: number;
  finecoPortfolioArtifacts: Record<string, any[]>; // SDK artifacts
  userSentFirstMessage: boolean;
  newAttachmentsAdded: boolean;
  newConstraintsAdded: boolean;
  openedConstraintsByThread: Record<string, string[]>;
  hasSentMessage: boolean;
  constraintsDefault: any;
  ptfThread: any;
};

type ActivityPanelStoreActions = {
  sendConstraintsStore: (constraints: any) => void;
  setConstraintsStore: (constraints: any, isFromBE?: boolean) => void;
  setParsedConfigConstraintsData: (
    parsedConfigConstraintsData: ConstraintSettingGeneric[],
  ) => void;
  setSavedConstraintsState: (constraints: any) => void;
  setConstraintSeen: (constraintType: string) => void;
  resetConstraintChangesUpdate: () => void;
  setIsPortfolioCreated: (portfolioCreation: PortfolioCreationType) => void;
  setPersistentConstraintsState: (constraints: any) => void;
  resetPersistentConstraintsState: () => void;
  setPreviousConstraintsStore: (constraints: any) => void;
  setConstraintsMainMenuRedDot: (redDot: boolean) => void;
  setAttachmentsRedDot: (redDot: boolean) => void;
  setFinancialPlanStore: (plan: number) => void;
  setFinecoPortfolioArtifact: (
    threadId: string,
    finecoPortfolioArtifact: Artifact,
  ) => void;
  setUserSentFirstMessage: (userSentFirstMessage: boolean) => void;
  setNewAttachmentsAdded: (newAttachmentsRedDot: boolean) => void;
  setNewConstraintsAdded: (newConstraintsAdded: boolean) => void;
  setOpenedConstraints: (threadId: string, constraints: string[]) => void;
  getOpenedConstraints: (threadId: string) => string[];
  setHasSentMessage: (useHasSentMessage: boolean) => void;
  fetchConstraintsDefault: () => void;
  fetchPtfByPortfolioData: (portfolioData: any) => void;
  resetStore: () => void;
};

const initialData: ActivityPanelStoreState = {
  constraintsState: null,
  parsedConfigConstraintsData: null,
  savedConstraintsState: null,
  constraintChangesUpdate: [],
  isPortfolioCreated: { messageId: "", hasCreated: false },
  portfolioMsgIds: [],
  persistentConstraintsState: [],
  savedSentConstraintsStore: null,
  constraintsMainMenuRedDot: false,
  attachmentsRedDot: false,
  portfoliosRedDot: false,
  financialPlanStore: -1,
  finecoPortfolioArtifacts: {},
  userSentFirstMessage: false,
  newAttachmentsAdded: false,
  newConstraintsAdded: false,
  openedConstraintsByThread: {},
  hasSentMessage: false,
  constraintsDefault: null,
  ptfThread: null,
};

const activityPanelStore = create<
  ActivityPanelStoreState & ActivityPanelStoreActions
>()((set, get) => ({
  ...initialData,
  sendConstraintsStore: (constraints: ConstraintState) => {
    //** these are user change */
    set({
      savedSentConstraintsStore: constraints,
    });
  },
  setParsedConfigConstraintsData: (
    parsedConfigConstraintsData: ConstraintSettingGeneric[],
  ) => {
    set({ parsedConfigConstraintsData: parsedConfigConstraintsData });
  },
  setConstraintsStore: (constraints: ConstraintState) => {
    get().resetPersistentConstraintsState();
    if (!constraints || !constraints.messageId || !constraints.data) {
      console.warn("Invalid constraints object received");
      return;
    }

    const currentState = get().constraintsState;

    if (constraints.messageId !== currentState?.messageId) {
      get().setFinancialPlanStore(
        constraints.data.risk.financial_plan as number,
      );
      set({
        constraintsState: {
          ...constraints,
          data: {
            ...constraints.data,
            asset: mergeAssetData(constraints.data),
          },
        },
      });
    }
  },
  setSavedConstraintsState: (constraints: any) => {
    if (get().savedConstraintsState) {
      //* we have already saved constraints, so things have changed */
      handleConstraintChange(get, set, constraints);
    } else {
      //** First time */
      applyInitialConstraints(get, set, constraints);
    }
  },
  setPersistentConstraintsState: (constraintsObject: any) => {
    set({
      persistentConstraintsState: {
        ...get().persistentConstraintsState,
        [constraintsObject.key]: constraintsObject.data,
      },
    });
  },
  resetPersistentConstraintsState: () => {
    set({ persistentConstraintsState: [] });
  },
  setConstraintSeen: (constraintType: string) => {
    set((state) => ({
      constraintChangesUpdate: state.constraintChangesUpdate.map((item) =>
        item.key === constraintType ? { ...item, seen: true } : item,
      ),
    }));
  },
  resetConstraintChangesUpdate: () => {
    set({ constraintChangesUpdate: [] });
  },
  setIsPortfolioCreated: (portfolioCreation) => {
    set({
      isPortfolioCreated: portfolioCreation,
    });
  },
  setPreviousConstraintsStore: (constraints: ConstraintState) => {
    set({
      constraintsState: constraints,
    });
  },
  setFinancialPlanStore: (plan) => {
    set({ financialPlanStore: plan });
  },
  setConstraintsMainMenuRedDot: (redDot) => {
    set({ constraintsMainMenuRedDot: redDot });
  },
  setAttachmentsRedDot: (redDot) => {
    set({ attachmentsRedDot: redDot });
  },
  setFinecoPortfolioArtifact: (threadId, finecoPortfolioArtifact) => {
    const existingArtifacts = get().finecoPortfolioArtifacts[threadId] || [];

    // Extract portfolio_id using shared helper
    const portfolioId = getPortfolioId(finecoPortfolioArtifact);

    if (!portfolioId) return;

    const isAlreadyAdded = existingArtifacts.some(
      (artifact) => {
        const existingPortfolioId = getPortfolioId(artifact);
        return existingPortfolioId === portfolioId;
      }
    );
    if (isAlreadyAdded) return;
    set({
      finecoPortfolioArtifacts: {
        ...get().finecoPortfolioArtifacts,
        [threadId]: [...existingArtifacts, finecoPortfolioArtifact],
      },
    });
  },
  setUserSentFirstMessage: (userSentFirstMessage) => {
    set({ userSentFirstMessage: userSentFirstMessage });
  },
  setNewAttachmentsAdded: (newAttachmentsAdded) => {
    set({ newAttachmentsAdded: newAttachmentsAdded });
  },
  setNewConstraintsAdded: (newConstraintsAdded) => {
    set({ newConstraintsAdded: newConstraintsAdded });
  },

  setOpenedConstraints: (threadId, constraints) => {
    set((state) => ({
      openedConstraintsByThread: {
        ...state.openedConstraintsByThread,
        [threadId]: constraints,
      },
    }));
  },
  getOpenedConstraints: (threadId) => {
    return get().openedConstraintsByThread[threadId] || [];
  },
  setHasSentMessage: (hasSentMessage) => {
    set({ hasSentMessage: hasSentMessage });
  },
  fetchConstraintsDefault: async () => {
    try {
      const constraintsDefaultResults = await ConstraintsService.fetchConstraintsDefault();
      set({
        constraintsDefault: constraintsDefaultResults,
      });
    } catch (error) {
      console.error("Error fetching constraints default:", error);
    }
  },
  fetchPtfByPortfolioData: async (portfolioData) => {
    const ptfResults = await ConstraintsService.fetchPtf(portfolioData);
    set({
      ptfThread: ptfResults,
    });
  },
  resetStore: () => {
    const currentConstraintsDefault = get().constraintsDefault;

    set({
      ...initialData,
      constraintsDefault: currentConstraintsDefault,
    });
  },
}));

export default activityPanelStore;
